import type {Editor} from 'grapesjs';
import importTouchInput from '@Utils/grapesjs-touch.util';
import type {EmailMakerPluginDeps} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/email-maker-plugin.types';
import {initCommands} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/commands';
import {importComponents} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/components';
import {importBlocks} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/blocks';
import {importStorage} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/storage';
import {addI18nMessages} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/i18n';
import {addRte, resetRteInitFlag} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/rte';
import {initEditorListeners} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/listeners/editor';
import {initComponentListeners} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/listeners/component';
import {initSelectionListeners} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/listeners/selection';
import {initStorageListeners} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/listeners/storage';
import {initStyleListeners} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/listeners/style';
import {initKeymapListeners} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/listeners/keymaps';
import {initAddElementButtons} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/add-element-buttons';
import {stopEmailMakerPolling} from '@EmailMakerReact/lib/email-maker-library';
import {initAiEditTeardown} from '@EmailMakerReact/lib/email-maker-ai-library';

/**
 * GrapesJS email maker plugin entry. initCommands must run before importComponents (component init
 * runs commands). Returns the master teardown: stops polling loops and removes every listener and keymap.
 */
export function installEmailMakerPlugin(editor: Editor, deps: EmailMakerPluginDeps): () => void {
  initCommands(editor);
  importComponents(editor, deps.componentsOptions);
  importBlocks(editor);
  importStorage(editor, deps.store, deps.savePayloadSources);
  addI18nMessages(editor);
  addRte(editor);

  const teardowns: (() => void)[] = [
    initAiEditTeardown(editor),
    initEditorListeners(editor, deps),
    initComponentListeners(editor),
    initSelectionListeners(editor),
    initStorageListeners(editor),
    initStyleListeners(editor),
    initKeymapListeners(editor),
    initAddElementButtons(editor),
  ];

  importTouchInput({editor});

  return (): void => {
    stopEmailMakerPolling(editor);
    resetRteInitFlag();
    teardowns.forEach((teardown) => {
      teardown();
    });
  };
}
